import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Download, Activity, Package, FileUp, Search, HardDrive, Clock, Shield, Edit, Trash2, Monitor } from 'lucide-react';
import { FaApple, FaWindows, FaLinux } from 'react-icons/fa';
import { useQueryClient } from '@tanstack/react-query';
import { useReleases, useReleaseAnalytics, useDeletedReleases } from '../api/hooks/updates';
import { useCreateRelease, useDeleteRelease } from '../api/hooks/updates/useReleaseMutation';
import { usePermissions } from '../hooks/usePermissions';
import { PermissionGate } from '../components/common/PermissionGate';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import {
  Button,
  Badge,
  Card,
  PageHeader,
  Input,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@license-management/ui';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { ReleaseUploadModal } from '../components/releases/ReleaseUploadModal';
import { ReleaseEditModal } from '../components/releases/ReleaseEditModal';
import { ReleaseAnalyticsDashboard } from '../components/releases/ReleaseAnalyticsDashboard';
import { API_BASE_URL } from '../api/config';

const ReleaseManagement: React.FC = () => {
  usePermissions(); // Load permissions for PermissionGate
  const queryClient = useQueryClient();
  const [selectedTab, setSelectedTab] = useState<'releases' | 'analytics'>('releases');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedRelease, setSelectedRelease] = useState<any>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [deleteConfirmRelease, setDeleteConfirmRelease] = useState<any>(null);
  
  // Filtering states
  const [selectedPlatform, setSelectedPlatform] = useState<string>('all');
  const [selectedChannel, setSelectedChannel] = useState<string>('all');
  const [selectedArchitecture, setSelectedArchitecture] = useState<string>('all');
  const [showInactive, setShowInactive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  const { data: releases = [], isLoading: isLoadingReleases, error: releasesError } = useReleases();
  const { data: analytics, isLoading: isLoadingAnalytics, error: analyticsError } = useReleaseAnalytics();
  const { data: trashData } = useDeletedReleases({ page: 1, pageSize: 1 });
  const trashCount = trashData?.totalCount || 0;
  const createRelease = useCreateRelease();
  const deleteRelease = useDeleteRelease();

  // Filter releases
  const filteredReleases = releases.filter(release => {
    if (selectedPlatform !== 'all' && release.platform !== selectedPlatform) return false;
    if (selectedChannel !== 'all' && release.channel !== selectedChannel) return false;
    if (selectedArchitecture !== 'all' && release.architecture !== selectedArchitecture) return false;
    if (!showInactive && !release.isActive) return false;
    if (searchQuery && !release.version.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !(release.releaseNotes || '').toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  // Download handler for releases - uses direct link for native browser download with progress
  const handleDownloadRelease = (releaseId: number) => {
    const url = `${API_BASE_URL}/api/v1/updates/admin/releases/${releaseId}/download`;

    // Create a temporary link and click it - browser handles the download natively
    // This provides proper download progress indication and handles large files efficiently
    const link = document.createElement('a');
    link.href = url;
    link.download = ''; // Browser will use Content-Disposition filename from server
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Group releases by version for better organization
  const releasesByVersion = filteredReleases.reduce((acc, release) => {
    if (!acc[release.version]) {
      acc[release.version] = [];
    }
    acc[release.version].push(release);
    return acc;
  }, {} as Record<string, typeof releases>);

  // Sort versions (newest first)
  const sortedVersions = Object.keys(releasesByVersion).sort((a, b) => {
    const aParts = a.split('.').map(Number);
    const bParts = b.split('.').map(Number);
    for (let i = 0; i < 3; i++) {
      if (aParts[i] !== bParts[i]) {
        return bParts[i] - aParts[i];
      }
    }
    return 0;
  });

  const formatFileSize = (bytes: number): string => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'win32':
      case 'windows':
        return <FaWindows size={24} color="#3b82f6" />;
      case 'darwin':
      case 'macos':
        return <FaApple size={24} color="#374151" />;
      case 'linux':
        return <FaLinux size={24} color="#ca8a04" />;
      default:
        return <Monitor className="w-6 h-6 text-gray-500" />;
    }
  };

  const getPlatformName = (platform: string) => {
    switch (platform) {
      case 'win32':
        return 'Windows';
      case 'darwin':
        return 'macOS';
      case 'linux':
        return 'Linux';
      default:
        return platform;
    }
  };

  const getFileType = (fileUrl: string): string | null => {
    const filename = fileUrl.split('/').pop() || '';
    const ext = filename.split('.').pop()?.toLowerCase();
    return ext || null;
  };

  const getFilename = (fileUrl: string): string => {
    return fileUrl.split('/').pop() || 'Unknown';
  };

  const getChannelVariant = (channel: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (channel) {
      case 'stable':
        return 'success';
      case 'beta':
        return 'warning';
      case 'alpha':
        return 'error';
      default:
        return 'default';
    }
  };

  if (isLoadingReleases || isLoadingAnalytics) {
    return <LoadingSpinner />;
  }

  const error = releasesError || analyticsError;
  if (error) {
    return <ErrorMessage message={error instanceof Error ? error.message : String(error)} />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Release Management"
        description="Manage software releases and updates"
        actions={
          <div className="flex items-center gap-2">
            <PermissionGate resource="releases" action="read">
              <Button
                variant="secondary"
                onClick={() => window.location.href = '/releases/trash'}
                leftIcon={<Trash2 className="h-4 w-4" />}
              >
                Recycle Bin
                {trashCount > 0 && (
                  <Badge variant="error" size="sm" className="ml-2">
                    {trashCount}
                  </Badge>
                )}
              </Button>
            </PermissionGate>
            <PermissionGate resource="releases" action="create">
              <Button
                onClick={() => setIsUploadModalOpen(true)}
                leftIcon={<FileUp className="h-4 w-4" />}
              >
                New Release
              </Button>
            </PermissionGate>
          </div>
        }
      />

      <Tabs value={selectedTab} onValueChange={(value: string) => setSelectedTab(value as 'releases' | 'analytics')}>
        <TabsList>
          <TabsTrigger value="releases">
            <Package className="h-4 w-4 mr-2" />
            Releases
          </TabsTrigger>
          <TabsTrigger value="analytics">
            <Activity className="h-4 w-4 mr-2" />
            Analytics
          </TabsTrigger>
        </TabsList>

        <TabsContent value="releases" className="mt-6">
          <div className="space-y-6">
            {/* Filters */}
            <Card className="p-4">
              {/* Search Bar */}
              <div className="mb-4">
                <Input
                  placeholder="Search by version or release notes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  leftElement={<Search className="h-4 w-4" />}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Platform</label>
                  <select
                    value={selectedPlatform}
                    onChange={(e) => setSelectedPlatform(e.target.value)}
                    className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="all">All Platforms</option>
                    <option value="win32">Windows</option>
                    <option value="darwin">macOS</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Channel</label>
                  <select
                    value={selectedChannel}
                    onChange={(e) => setSelectedChannel(e.target.value)}
                    className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="all">All Channels</option>
                    <option value="stable">Stable</option>
                    <option value="beta">Beta</option>
                    <option value="alpha">Alpha</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Architecture</label>
                  <select
                    value={selectedArchitecture}
                    onChange={(e) => setSelectedArchitecture(e.target.value)}
                    className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="all">All Architectures</option>
                    <option value="x64">x64</option>
                    <option value="arm64">ARM64</option>
                    <option value="ia32">x86</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={showInactive}
                      onChange={(e) => setShowInactive(e.target.checked)}
                      className="rounded border-zinc-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-zinc-700">Show inactive</span>
                  </label>
                </div>
              </div>
            </Card>

            {/* Stats Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="p-4">
                <p className="text-sm text-zinc-500">Total Releases</p>
                <p className="text-2xl font-semibold text-zinc-900">{releases.length}</p>
              </Card>
              <Card className="p-4">
                <p className="text-sm text-zinc-500">Active Releases</p>
                <p className="text-2xl font-semibold text-zinc-900">{releases.filter(r => r.isActive).length}</p>
              </Card>
              <Card className="p-4">
                <p className="text-sm text-zinc-500">Unique Versions</p>
                <p className="text-2xl font-semibold text-zinc-900">{Object.keys(releasesByVersion).length}</p>
              </Card>
              <Card className="p-4">
                <p className="text-sm text-zinc-500">Storage Used</p>
                <p className="text-2xl font-semibold text-zinc-900">
                  {formatFileSize(releases.reduce((sum, r) => sum + r.fileSize, 0))}
                </p>
              </Card>
            </div>

            {filteredReleases.length === 0 ? (
              <Card className="p-8 text-center">
                <p className="text-zinc-500">No releases found matching your filters.</p>
              </Card>
            ) : (
              <div className="space-y-6">
                {sortedVersions.map((version) => (
                  <Card key={version} className="overflow-hidden">
                    <div className="bg-zinc-50 px-4 py-3 border-b border-zinc-200">
                      <h3 className="text-lg font-medium text-zinc-900">Version {version}</h3>
                    </div>
                    <ul className="divide-y divide-zinc-200">
                      {releasesByVersion[version].map((release) => (
                        <li key={release.id}>
                          <div className="px-4 py-4 sm:px-6 hover:bg-zinc-50 transition-colors">
                            <div className="flex items-start justify-between">
                              <div className="flex gap-4 flex-1">
                                {/* Platform Icon */}
                                <div className="flex-shrink-0">
                                  <div className="w-12 h-12 bg-zinc-100 rounded-lg flex items-center justify-center">
                                    {getPlatformIcon(release.platform)}
                                  </div>
                                </div>

                                {/* Release Info */}
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <h4 className="text-base font-medium text-zinc-900">
                                      {getPlatformName(release.platform)}
                                      {release.architecture !== 'x64' && ` (${release.architecture})`}
                                    </h4>
                                    <Badge variant={getChannelVariant(release.channel)} size="sm">
                                      {release.channel}
                                    </Badge>
                                    {getFileType(release.fileUrl) && (
                                      <Badge variant="default" size="sm" className="font-mono">
                                        {getFileType(release.fileUrl)?.toUpperCase()}
                                      </Badge>
                                    )}
                                    {release.isDraft && (
                                      <Badge variant="warning" size="sm">Draft</Badge>
                                    )}
                                    {release.isMandatory && (
                                      <Badge variant="primary" size="sm">
                                        <Shield className="h-3 w-3 mr-1" />
                                        Mandatory
                                      </Badge>
                                    )}
                                    {!release.isActive && (
                                      <Badge variant="default" size="sm">Inactive</Badge>
                                    )}
                                  </div>

                                  {release.releaseNotes && (
                                    <p className="mt-1 text-sm text-zinc-600 line-clamp-2">{release.releaseNotes}</p>
                                  )}

                                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-500">
                                    <span className="flex items-center gap-1">
                                      <Clock className="h-3 w-3" />
                                      {formatDistanceToNow(new Date(release.releaseDate), { addSuffix: true })}
                                    </span>
                                    <span className="flex items-center gap-1">
                                      <HardDrive className="h-3 w-3" />
                                      {formatFileSize(release.fileSize)}
                                    </span>
                                    <span className="font-mono">
                                      SHA: {release.sha512.substring(0, 8)}...
                                    </span>
                                    {release.minimumAppVersion && (
                                      <span className="text-zinc-600">
                                        Min Version: {release.minimumAppVersion}
                                      </span>
                                    )}
                                  </div>
                                  <div className="mt-1 text-xs text-zinc-400 font-mono truncate" title={getFilename(release.fileUrl)}>
                                    {getFilename(release.fileUrl)}
                                  </div>
                                </div>
                              </div>

                              {/* Actions */}
                              <div className="flex items-center gap-2 ml-4">
                                <PermissionGate resource="releases" action="update">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setSelectedRelease(release);
                                      setIsEditModalOpen(true);
                                    }}
                                    title="Edit release & configure rollout"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                </PermissionGate>
                                <PermissionGate resource="releases" action="delete">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setDeleteConfirmRelease(release)}
                                    title="Delete release"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </PermissionGate>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDownloadRelease(release.id)}
                                  title="Download release"
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="analytics" className="mt-6">
          <ReleaseAnalyticsDashboard analytics={analytics!} />
        </TabsContent>
      </Tabs>

      {/* Upload Modal */}
      <ReleaseUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onSubmit={async (data) => {
          // If data has an 'id' property, it's already a created release
          if ('id' in data) {
            // Release already created during upload, just close modal
            setIsUploadModalOpen(false);
            // Invalidate queries to refresh the list
            queryClient.invalidateQueries({ queryKey: ['releases'] });
          } else {
            // Create release (fallback case)
            await createRelease.mutateAsync(data);
            setIsUploadModalOpen(false);
          }
        }}
        isSubmitting={createRelease.isPending}
      />


      {/* Edit Release Modal (includes rollout configuration) */}
      {selectedRelease && (
        <ReleaseEditModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedRelease(null);
          }}
          release={selectedRelease}
          onSuccess={() => {
            setIsEditModalOpen(false);
            setSelectedRelease(null);
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!deleteConfirmRelease}
        onCancel={() => setDeleteConfirmRelease(null)}
        onConfirm={async () => {
          if (deleteConfirmRelease) {
            await deleteRelease.mutateAsync({ releaseId: deleteConfirmRelease.id });
            setDeleteConfirmRelease(null);
          }
        }}
        title="Delete Release"
        message={`Are you sure you want to delete release ${deleteConfirmRelease?.version} for ${deleteConfirmRelease?.platform}? The release will be moved to the recycle bin and permanently deleted after 30 days.`}
        confirmLabel="Move to Recycle Bin"
        severity="normal"
      />
    </div>
  );
};

export default ReleaseManagement;